import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CalculationsUIState, TableSortOrder, ViewMode } from '@packages/types';
import { SortOption, TabOption } from '@packages/types';
import { DEFAULT_PAGE_SIZE_CALCULATIONS, DEFAULT_METRICS_PAGE_SIZE, DEFAULT_TAB } from '../constants';
import { DEFAULT_SORT } from '@ui/constants';

const initialState: CalculationsUIState = {
    activeTab: DEFAULT_TAB,
    viewMode: 'card',

    calculationsTab: {
        selectedCategories: [],
        categoriesInitialized: false,
        searchValue: '',
        sortValue: DEFAULT_SORT,
        currentPage: 1,
        pageSize: DEFAULT_PAGE_SIZE_CALCULATIONS,
    },

    metricsTab: {
        selectedMetrics: [],
        selectedCohort: {
            id: 'all',
            label: 'All Patients',
        },
        searchValue: '',
        sortField: undefined,
        sortOrder: undefined,
        currentPage: 1,
        pageSize: DEFAULT_METRICS_PAGE_SIZE,
    },

    detailsModal: {
        selectedCohort: {
            id: 'all',
            label: 'All Patients',
        },
    },
};

const calculationsUISlice = createSlice({
    name: 'calculationsUI',
    initialState,
    reducers: {
        // ==================== Shared Actions ====================
        setActiveTab: (state, action: PayloadAction<TabOption>) => {
            state.activeTab = action.payload;
        },
        setViewMode: (state, action: PayloadAction<ViewMode>) => {
            state.viewMode = action.payload;
        },

        // ==================== Calculations Tab Actions ====================
        setSelectedCategories: (state, action: PayloadAction<string[]>) => {
            state.calculationsTab.selectedCategories = action.payload;
            state.calculationsTab.categoriesInitialized = true;
            state.calculationsTab.currentPage = 1;
        },
        setSearchValue: (state, action: PayloadAction<string>) => {
            state.calculationsTab.searchValue = action.payload;
            state.calculationsTab.currentPage = 1;
        },
        setSortValue: (state, action: PayloadAction<SortOption>) => {
            state.calculationsTab.sortValue = action.payload;
            state.calculationsTab.currentPage = 1;
        },
        setCurrentPage: (state, action: PayloadAction<number>) => {
            state.calculationsTab.currentPage = action.payload;
        },
        setPageSize: (state, action: PayloadAction<number>) => {
            state.calculationsTab.pageSize = action.payload;
            state.calculationsTab.currentPage = 1;
        },

        // ==================== Metrics Tab Actions ====================
        setSelectedMetrics: (state, action: PayloadAction<number[]>) => {
            state.metricsTab.selectedMetrics = action.payload;
        },
        setSelectedCohort: (state, action: PayloadAction<{ id: string; label: string }>) => {
            state.metricsTab.selectedCohort = action.payload;
            state.metricsTab.currentPage = 1;
        },
        setMetricsSearchValue: (state, action: PayloadAction<string>) => {
            state.metricsTab.searchValue = action.payload;
            state.metricsTab.currentPage = 1;
        },
        setMetricsSortField: (state, action: PayloadAction<string | undefined>) => {
            state.metricsTab.sortField = action.payload;
            state.metricsTab.currentPage = 1;
        },
        setMetricsSortOrder: (state, action: PayloadAction<TableSortOrder | undefined>) => {
            state.metricsTab.sortOrder = action.payload;
            state.metricsTab.currentPage = 1;
        },
        setMetricsCurrentPage: (state, action: PayloadAction<number>) => {
            state.metricsTab.currentPage = action.payload;
        },
        setMetricsPageSize: (state, action: PayloadAction<number>) => {
            state.metricsTab.pageSize = action.payload;
            state.metricsTab.currentPage = 1;
        },

        // ==================== Details Modal Actions ====================
        setDetailsModalCohort: (state, action: PayloadAction<{ id: string; label: string }>) => {
            state.detailsModal.selectedCohort = action.payload;
        },

        // ==================== Reset ====================
        resetUIState: () => initialState,
    },
});

export const {
    setActiveTab,
    setViewMode,
    setSelectedCategories,
    setSearchValue,
    setSortValue,
    setCurrentPage,
    setPageSize,
    setSelectedMetrics,
    setSelectedCohort,
    setMetricsSearchValue,
    setMetricsSortField,
    setMetricsSortOrder,
    setMetricsCurrentPage,
    setMetricsPageSize,
    setDetailsModalCohort,
    resetUIState,
} = calculationsUISlice.actions;

export default calculationsUISlice.reducer;
